import React from "react";
import { useNotification } from "@context/NotificationContext";
import {
  getTerminalServiceErrorMessage,
  useTerminalServiceDocumentUpload,
} from "@features/terminal-services/hooks/useTerminalServiceDocumentUpload";
import type { BypassServiceDocument } from "@services/terminal-services/types";
import {
  createBypassServiceRequest,
  resubmitBypassServiceRequest,
} from "@services/terminalServicesApi";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FOTT_SERVICE_REQUEST_FIELDS as F } from "../constants";
import { mapBypassServiceFormToPayload } from "../mappers";
import type { FottServiceRequestFormData } from "../schema";

const isFileAttachment = (value: unknown): value is File =>
  typeof File !== "undefined" && value instanceof File;

const getUploadedAttachmentDocuments = (
  formData: FottServiceRequestFormData,
): BypassServiceDocument[] | null => {
  return (
    (formData[F.ATTACHMENT]?.filter(
      (attachment) => !isFileAttachment(attachment) && !!attachment.documentUrl,
    ) as BypassServiceDocument[] | undefined) ?? null
  );
};

export const useCreateBypassServiceRequest = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const {
    uploadEntries: attachmentFiles,
    setUploadEntries: setAttachmentFiles,
    handleUpload: handleAttachmentUpload,
    resetUpload: resetAttachmentUpload,
    isUploading: isUploadingAttachment,
  } = useTerminalServiceDocumentUpload({
    storeKey: F.ATTACHMENT,
    certificateType: "",
    fallbackFileNamePrefix: "bypass-service-attachment",
    errorNotification: {
      titleKey:
        "terminal-services.fott-service-request.notifications.attachment-upload-error-title",
      descriptionKey:
        "terminal-services.fott-service-request.notifications.attachment-upload-error-description",
    },
  });

  const createRequestMutation = useMutation({
    mutationFn: createBypassServiceRequest,
    onSuccess: () => {
      notify({
        title: t("terminal-services.fott-service-request.notifications.submit-success-title"),
        type: "success",
      });
    },
    onError: (error) => {
      notify({
        title: t("terminal-services.fott-service-request.notifications.submit-error-title"),
        description:
          getTerminalServiceErrorMessage(error) ??
          t("terminal-services.fott-service-request.notifications.submit-error-description"),
        type: "error",
      });
      console.error("Create Pigging service request failed:", error);
    },
  });

  const resubmitRequestMutation = useMutation({
    mutationFn: resubmitBypassServiceRequest,
    onSuccess: () => {
      notify({
        title: t("terminal-services.fott-service-request.notifications.submit-success-title"),
        type: "success",
      });
    },
    onError: (error) => {
      notify({
        title: t("terminal-services.fott-service-request.notifications.submit-error-title"),
        description:
          getTerminalServiceErrorMessage(error) ??
          t("terminal-services.fott-service-request.notifications.submit-error-description"),
        type: "error",
      });

      console.error("Resubmit bypass service request failed:", error);
    },
  });

  const resubmitRequest = React.useCallback(
    async (id: string, formValues: FottServiceRequestFormData) => {
      try {
        const payload = mapBypassServiceFormToPayload({
          values: formValues,
          attachments: getUploadedAttachmentDocuments(formValues),
        });

        await resubmitRequestMutation.mutateAsync({ id, payload });
        return true;
      } catch {
        return false;
      }
    },
    [resubmitRequestMutation],
  );

  const submitRequest = React.useCallback(
    async (formData: FottServiceRequestFormData) => {
      try {
        const attachmentDocuments = getUploadedAttachmentDocuments(formData);
        const payload = mapBypassServiceFormToPayload({
          values: formData,
          attachments: attachmentDocuments,
        });

        await createRequestMutation.mutateAsync(payload);
        return true;
      } catch {
        return false;
      }
    },
    [createRequestMutation],
  );

  return {
    attachmentFiles,
    setAttachmentFiles,
    handleAttachmentUpload,
    removeAttachment: resetAttachmentUpload,
    resetAttachmentUpload,
    isUploadingAttachment,
    isSubmitting:
      createRequestMutation.isPending || resubmitRequestMutation.isPending || isUploadingAttachment,
    submitRequest,
    resubmitRequest,
  };
};
